// Story 10.2 — Bot Card: header con nombre + status, métricas, footer con
// acciones. `React.memo` para que un evento WS de un bot no re-renderice
// las otras cards.

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Loader2, MoreVertical, Trash2 } from 'lucide-react';
import { memo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { useBotStats } from '@/hooks/queries/use-bot-stats';
import { useBotRealtimeMetrics } from '@/hooks/use-bot-realtime-metrics';
import type { BotRowSummary } from '@/lib/api-client';
import { botsApi } from '@/lib/api-client';
import { formatRelative } from '@/lib/relative-time';
import { cn } from '@/lib/utils';
import { BotMetric } from './BotMetric';
import { BotStatusBadge } from './BotStatusBadge';

interface Props {
  bot: BotRowSummary;
}

function BotCardImpl({ bot }: Props): React.ReactElement {
  const stats = useBotStats(bot.id, 'today');
  const realtime = useBotRealtimeMetrics(bot.id, bot.name, {
    // P-231: cuando el snapshot del backend cambia (`dataUpdatedAt` salta
    // cada refetch exitoso), reseteamos los deltas para que la suma no
    // double-count los eventos que ya están en el snapshot fresco.
    resetSignal: stats.dataUpdatedAt,
    // IG-20: AC #3 pide "toast + badge" en `flow:error`. El badge ya lo
    // pinta el `errorPulse` del reducer; el toast lo disparamos acá.
    onFlowError: () => {
      toast.error(`El bot "${bot.name}" reportó un error en su flow.`);
    },
  });
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState(false);

  // Combina stats del backend (snapshot) con deltas en vivo del WS.
  const conversations = (stats.data?.conversations ?? 0) + realtime.conversations;
  const received = (stats.data?.messagesReceived ?? 0) + realtime.received;
  const sent = (stats.data?.messagesSent ?? 0) + realtime.sent;

  // P-236: si el snapshot todavía no llegó (loading) o falló (error 5xx,
  // network), mostramos `—` en vez de `0` para no mentirle al usuario.
  // `realtime.X` solo es confiable cuando hay snapshot que sumarle.
  const showDash = stats.isLoading || stats.isError;
  const effectiveStatus = realtime.statusOverride ?? bot.status;

  async function handleDelete(): Promise<void> {
    if (!confirm(`¿Borrar el bot "${bot.name}"? Esta acción es irreversible.`)) return;
    setDeleting(true);
    try {
      await botsApi.remove(bot.id);
      await qc.invalidateQueries({ queryKey: ['bots'] });
      toast.success(`Bot "${bot.name}" borrado.`);
    } catch (err) {
      toast.error(`No se pudo borrar el bot: ${(err as Error).message}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card
      className={cn(
        'transition-shadow hover:shadow-md',
        realtime.errorPulse && 'ring-2 ring-destructive/50',
      )}
      data-bot-id={bot.id}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold">{bot.name}</h3>
            {bot.phoneNumberId ? (
              <p className="truncate font-mono text-xs text-muted-foreground">
                {bot.phoneNumberId}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Sin número asignado</p>
            )}
          </div>
          <BotStatusBadge status={effectiveStatus} pulse={realtime.errorPulse} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2">
          <BotMetric
            label={'Conversa\u00ADciones'}
            value={showDash ? '—' : conversations}
            delta={realtime.conversations}
          />
          <BotMetric
            label="Recibidos"
            value={showDash ? '—' : received}
            delta={realtime.received}
          />
          <BotMetric label="Enviados" value={showDash ? '—' : sent} delta={realtime.sent} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Última actualización: {formatRelative(bot.updatedAt)}
        </p>
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-2">
        <Button asChild>
          <Link to="/bots/$botId" params={{ botId: String(bot.id) }}>
            Abrir
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDelete}
          disabled={deleting}
          aria-label={`Borrar bot ${bot.name}`}
        >
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="icon" aria-label="Más acciones">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * `React.memo` con comparación por id+updatedAt: una card solo re-renderiza
 * cuando su propio bot cambió, no cuando otra card recibe un evento.
 */
export const BotCard = memo(BotCardImpl, (prev, next) => {
  return (
    prev.bot.id === next.bot.id &&
    prev.bot.updatedAt === next.bot.updatedAt &&
    prev.bot.status === next.bot.status &&
    prev.bot.name === next.bot.name &&
    prev.bot.phoneNumberId === next.bot.phoneNumberId
  );
});
